import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterModule } from '@angular/router';
import {
    DatepickerComponent,
    DateUtilService,
    LoadingOverlayComponent,
    MAT_ICON,
    PaginationComponent,
    SelectComponent,
    SelectOption,
    SessionPreferenceService,
    TOAST_STATE,
    ToastmessageService,
    TranslationPipe,
    TranslationService
} from '@prekog/common-ui';
import { Observable } from 'rxjs';

import { FUTURE_DATE_ERROR } from '../../shared/adminconstants';
import { ProductStatisticTotals } from '../../shared/models/product-statistic-totals.model';
import { ReferenceDataService } from '../../shared/reference-data.service';
import { UtilService } from '../../shared/util.service';
import { CalculateProductStatisticTotalsUseCase } from '../../use-cases/calculateproductstatistictotals/calculate-product-statistic-totals.usecase';
import { GetProductStatisticTotalsRequest } from '../../use-cases/loadproductglobaltotals/get-product-statistic-totals.request';
import { LoadProductGlobalTotalsUseCase } from '../../use-cases/loadproductglobaltotals/load-product-global-totals.usecase';
import { LoadProductStatisticUseCase } from '../../use-cases/loadproductstatistic/load-product-statistic.usecase';
import { StatisticsFilter } from '../statisticsfilter.interface';
import { GetProductStatisticRequest } from './getproductstatistic.request';
import { GetProductStatisticResponse } from './getproductstatistic.response';
import { ProductStatistics } from './productstatistics.model';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-productstatistic',
    imports: [
        MatIconModule,
        DatepickerComponent,
        SelectComponent,
        TranslationPipe,
        ReactiveFormsModule,
        CommonModule,
        RouterModule,
        PaginationComponent,
        LoadingOverlayComponent
    ],
    templateUrl: './productstatistic.component.html',
    styleUrl: './productstatistic.component.scss'
})
export class ProductStatisticComponent implements OnInit {
    defaultEndDate!: string;
    defaultStartDate!: string;
    endDate = new Date();
    getStockingStatisticsResponse!: GetProductStatisticResponse;
    readonly globalTotals = signal<ProductStatisticTotals | undefined>(undefined);
    maxDate!: string;
    readonly page = signal(0);
    productOptions: SelectOption[] = [];
    productStatisticsFilter!: FormGroup;
    readonly savedEndDate = signal<string>('');
    readonly savedProductIds = signal<number[]>([]);
    readonly savedStartDate = signal<string>('');
    readonly selectedRowIndex = signal<number | null>(null);
    readonly size = signal(25);
    startDate = new Date();
    readonly totalItems = signal(0);
    readonly totals = signal<ProductStatisticTotals | undefined>(undefined);

    private _loadUseCase = inject(LoadProductStatisticUseCase);
    private _loadGlobalTotalsUseCase = inject(LoadProductGlobalTotalsUseCase);
    private _calculateTotalsUseCase = inject(CalculateProductStatisticTotalsUseCase);
    private _cdr = inject(ChangeDetectorRef);
    private _dateUtilService = inject(DateUtilService);
    private _referenceData = inject(ReferenceDataService);
    private _sessionPreference = inject(SessionPreferenceService);
    private _fb = inject(FormBuilder);
    private _router = inject(Router);
    private _toastMessageService = inject(ToastmessageService);
    private _translateService = inject(TranslationService);
    private _utilService = inject(UtilService);

    private readonly _morteBagProductId = 1260;

    ngOnInit(): void {
        if (!sessionStorage.getItem('token')) {
            void this._router.navigateByUrl('/');
        } else {
            const dateDefaults = this._dateUtilService.getDefaultDateFilterValues('2023-01-01');
            this.defaultStartDate = dateDefaults.defaultStartDate;
            this.defaultEndDate = dateDefaults.defaultEndDate;
            this.startDate = dateDefaults.startDate;
            this.endDate = dateDefaults.endDate;
            this.maxDate = this._dateUtilService.getMaxDateForDateSelection(true);
            this.savedStartDate.set(this.defaultStartDate);
            this.savedEndDate.set(this.defaultEndDate);
            this._setDefaultFilters();
            const savedFilters = this._sessionPreference.load<StatisticsFilter>('productstatistic-filters');
            if (savedFilters) {
                this.productStatisticsFilter.patchValue(savedFilters);
                this.savedProductIds.set(savedFilters.productIds ?? []);
                this.savedStartDate.set(savedFilters.startDate ? String(savedFilters.startDate) : '');
                this.savedEndDate.set(savedFilters.endDate ? String(savedFilters.endDate) : '');
            }
            this._loadProducts();
            this.updateStockingStatisticsByFilter();
        }
    }

    onProductIdsChange(selectedIds: (number | string)[]): void {
        this.productStatisticsFilter.patchValue({ productIds: selectedIds });
    }

    onStartDateChange(date: string): void {
        this.productStatisticsFilter.patchValue({ startDate: date });
    }

    onEndDateChange(date: string): void {
        this.productStatisticsFilter.patchValue({ endDate: date });
    }

    updateStockingStatisticsByFilter(): void {
        this.page.set(0);
        this._sessionPreference.save('productstatistic-filters', this.productStatisticsFilter.value);
        const getStockingStatisticsRequest = this._createGetStockingStatisticsRequest();

        this._loadStockingStatistics(getStockingStatisticsRequest, this.selectedRowIndex() !== null);
        this.selectedRowIndex.set(null);
        this._loadGlobalTotals();
    }

    onResetFilters(): void {
        this.page.set(0);
        this.size.set(25);
        this._sessionPreference.remove('productstatistic-filters');
        this._setDefaultFilters();
        this.savedProductIds.set([]);
        this.savedStartDate.set(this.defaultStartDate);
        this.savedEndDate.set(this.defaultEndDate);
        this.updateStockingStatisticsByFilter();
    }

    selectRow(index: number): void {
        const statistics = this.getStockingStatisticsResponse?.productStatisticsList;
        if (!statistics) {
            return;
        }

        if (this.selectedRowIndex() === index) {
            statistics[index].expanded = false;
            this.selectedRowIndex.set(null);
        } else {
            const previousIndex = this.selectedRowIndex();
            if (previousIndex !== null && statistics[previousIndex]) {
                statistics[previousIndex].expanded = false;
            }
            statistics[index].expanded = true;
            this.selectedRowIndex.set(index);
        }

        this._refreshTotals();
    }

    private _refreshTotals(): void {
        const selectedIndex = this.selectedRowIndex();
        const statistics = this.getStockingStatisticsResponse?.productStatisticsList;

        if (selectedIndex !== null && statistics?.[selectedIndex]) {
            const rowTotals = this._calculateTotalsUseCase.execute({ list: statistics, selectedIndex });
            this.totals.set(rowTotals as ProductStatisticTotals);
        } else {
            this.totals.set(this.globalTotals());
        }
    }

    onPageChanged(event: { page: number; size: number }): void {
        this.page.set(event.page);
        this.size.set(event.size);
        const request = this._createGetStockingStatisticsRequest();
        this._loadStockingStatistics(request, this.selectedRowIndex() !== null);
        this.selectedRowIndex.set(null);
    }

    private _showFutureDateError(): void {
        this._toastMessageService.displayToastMessage(
            TOAST_STATE.error,
            this._translateService.translate(FUTURE_DATE_ERROR),
            MAT_ICON.error
        );
    }

    private _createGetStockingStatisticsRequest(): GetProductStatisticRequest {
        const filterValues = this.productStatisticsFilter.value as StatisticsFilter;

        return {
            productIds: this._utilService.nullIfEmpty(filterValues.productIds),
            fromDate: filterValues.startDate,
            toDate: filterValues.endDate,
            page: this.page(),
            size: this.size()
        };
    }

    private _setDefaultFilters(): void {
        const futureDateValidator = this._dateUtilService.futureDateValidator(() => this._showFutureDateError());

        this.productStatisticsFilter = this._fb.group({
            fromStorageType: [null],
            toStorageType: [null],
            productIds: [[]],
            startDate: [this.defaultStartDate, [Validators.required, futureDateValidator]],
            endDate: [this.defaultEndDate, [Validators.required, futureDateValidator]]
        });
    }

    private _loadStockingStatistics(getStockingStatisticsRequest: GetProductStatisticRequest, refreshGlobalTotals: boolean): void {
        (this._loadUseCase.execute(getStockingStatisticsRequest) as Observable<GetProductStatisticResponse>).subscribe((response) => {
            response.productStatisticsList = this._excludeMorteBagProduct(response.productStatisticsList);
            response.productStatisticsList?.forEach((item) => {
                item.productStatisticsPerPartnerList = item.productStatisticsPerPartnerList?.filter(
                    (partner) =>
                        (partner.delegatedQuantity ?? 0) !== 0 ||
                        (partner.soldFromDelegationQuantity ?? 0) !== 0 ||
                        (partner.soldToPartnerQuantity ?? 0) !== 0 ||
                        (partner.returnedFromDelegationQuantity ?? 0) !== 0 ||
                        (partner.stockQuantity ?? 0) !== 0
                );
                item.productStatisticsPerFestivalList = item.productStatisticsPerFestivalList?.filter(
                    (festival) =>
                        (festival.movedQuantity ?? 0) !== 0 ||
                        (festival.soldQuantity ?? 0) !== 0 ||
                        (festival.returnedQuantity ?? 0) !== 0
                );
            });
            this.getStockingStatisticsResponse = response;
            this.totalItems.set(response.totalItems ?? 0);
            this.selectedRowIndex.set(null);
            if (!refreshGlobalTotals) {
                this._refreshTotals();
            }
            this._cdr.markForCheck();
        });
    }

    private _loadGlobalTotals(): void {
        const filterValues = this.productStatisticsFilter.value as StatisticsFilter;
        const totalsRequest: GetProductStatisticTotalsRequest = {
            fromDate: filterValues.startDate,
            toDate: filterValues.endDate,
            productIds: this._utilService.nullIfEmpty(filterValues.productIds)
        };
        (this._loadGlobalTotalsUseCase.execute(totalsRequest) as Observable<ProductStatisticTotals>).subscribe((totals) => {
            this.globalTotals.set(totals);
            this._refreshTotals();
            this._cdr.markForCheck();
        });
    }

    private _excludeMorteBagProduct(productStatisticsList?: ProductStatistics[]): ProductStatistics[] | undefined {
        return productStatisticsList?.filter((item) => item.productId !== this._morteBagProductId);
    }

    private _loadProducts(): void {
        this._referenceData.loadProducts().subscribe((products) => {
            this.productOptions = products.map((product) => ({
                id: product.id!,
                name: `${product.title} - ${product.author} [${product.isbn}]`
            }));
            this._cdr.markForCheck();
        });
    }
}
